import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Stack,
    Paper,
    Divider,
    IconButton,
    Chip
} from '@mui/material';
import {
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Pause as PauseIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { $authHost } from '../../../http';
import { updateTask, fetchTasks } from '../../../store/reducers/tasksReducer';
import { selectTasksByObject } from '../../../store/selectors/tasksSelector';
import { allowedTransitions, statusDefs } from '../../contents/TasksViewNew';

const TaskTab = ({ 
    mark,
    darkMode = false,
    map = null
}) => {
    const dispatch = useDispatch();
    const auth = useAuth();
    const user = useSelector(state => state.state.user);
    const usersSettings = useSelector(state => state.state.usersSettings);
    
    // Получаем тип объекта для селектора
    const getObjectType = () => {
        // Проверяем тип по mark.type
        if (mark.type) {
            if (mark.type.includes('pillar')) {
                return 'pillars';
            }
            if (mark.type.includes('fider') || mark.type === 'fiders') {
                return 'fiders';
            }
            // Для других типов (zvz, opn, rlnd)
            return mark.type;
        }
        
        // Если type не определен, пытаемся определить по другим признакам
        if (mark.layer) {
            // Если есть layer, это скорее всего фидер
            return 'fiders';
        }
        
        // По умолчанию считаем опорой
        return 'pillars';
    };

    // Формируем ID фидера в формате задачи: {fider.id}_{object1.type}_{object1.id}_{object2.type}_{object2.id}
    const getFiderTaskId = React.useMemo(() => {
        const objectType = getObjectType();
        if (objectType !== 'fiders') return null;
        
        // Для фидеров нужен специальный формат ID
        if (typeof mark.segment === "number" && mark.connected && mark.segment < mark.connected.length - 1) {
            const object1 = mark.connected[mark.segment];
            const object2 = mark.connected[mark.segment + 1];
            if (!object1 || !object2) return null;
            const fiderId = mark.fid || mark.id;
            return `${fiderId}_${object1.type}_${object1.id}_${object2.type}_${object2.id}`;
        }
        
        // Если segment не определен, но есть points (для режима создания задачи по точкам)
        if (mark.points && mark.points.length === 2) {
            const object1 = mark.points[0];
            const object2 = mark.points[1];
            if (!object1 || !object2) return null;
            const fiderId = mark.fid || mark.id;
            return `${fiderId}_${object1.type}_${object1.id}_${object2.type}_${object2.id}`;
        }
        
        return null;
    }, [mark]);

    const objectType = getObjectType();
    const objectId = getFiderTaskId || mark.fid || mark.id;
    
    // Получаем задачу через селектор
    const task = useSelector(state => selectTasksByObject(state, objectType, objectId));
    
    // Получаем ID постановщика
    const getCreatorId = () => {
        if (!task?.creator) return null;
        
        // Если creator - это объект с полем id
        if (typeof task.creator === 'object') {
            return task.creator.id || null;
        }
        
        // Если creator - это ID
        if (typeof task.creator === 'number') {
            return task.creator;
        }
        
        return null;
    };

    // Получаем ID ответственного
    const getResponsibleId = () => {
        if (!task?.responsible) return null;
        
        // Если responsible - это объект с полем id
        if (typeof task.responsible === 'object') {
            return task.responsible.id || null;
        }
        
        // Если responsible - это ID
        if (typeof task.responsible === 'number') {
            return task.responsible;
        }
        
        return null;
    };

    // Получаем имя постановщика
    const getCreatorName = () => {
        if (!task?.creator) return 'Не указано';
        
        // Если creator - это объект с полями name или display_name
        if (typeof task.creator === 'object') {
            return task.creator.name || task.creator.display_name || 'Не указано';
        }
        
        // Если creator - это ID, ищем в usersSettings
        if (typeof task.creator === 'number' && usersSettings) {
            const creatorUser = usersSettings.find(u => u.id === task.creator);
            return creatorUser?.name || creatorUser?.display_name || `ID: ${task.creator}`;
        }
        
        return 'Не указано';
    };
    
    const [resultText, setResultText] = useState('');
    const [isEditingResult, setIsEditingResult] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [postponeReason, setPostponeReason] = useState('');
    const [showRejectField, setShowRejectField] = useState(false);
    const [showPostponeField, setShowPostponeField] = useState(false);
    const [loading, setLoading] = useState(false);

    // Инициализация результата из задачи
    useEffect(() => {
        if (task?.data?.results?.[`${objectType}_${objectId}`]) {
            const currentResult = task.data.results[`${objectType}_${objectId}`];
            if (Array.isArray(currentResult) && currentResult.length > 0) {
                // Берем последний результат
                setResultText(currentResult[currentResult.length - 1].text || '');
            } else if (typeof currentResult === 'string') {
                setResultText(currentResult);
            }
        }
    }, [task, objectType, objectId]);

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Форматирование даты и времени
    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Получить историю результатов
    const getResultHistory = () => {
        if (!task?.data?.results?.[`${objectType}_${objectId}`]) return [];
        const results = task.data.results[`${objectType}_${objectId}`];
        if (Array.isArray(results)) {
            return results;
        }
        return [];
    };

    // Сохранить результат в историю
    const saveResult = async () => {
        if (!resultText.trim() || !task) return;

        setLoading(true);
        try {
            const resultHistory = getResultHistory();
            const newResultEntry = {
                text: resultText.trim(),
                date: new Date().toISOString(),
                userId: user.vio_id || user.id,
                userName: user.display_name || user.short_fio || 'Неизвестный пользователь'
            };

            const updatedResults = [...resultHistory, newResultEntry];
            
            const updatedData = {
                ...task.data,
                results: {
                    ...(task.data?.results || {}),
                    [`${objectType}_${objectId}`]: updatedResults
                }
            };

            const updatedTask = {
                ...task,
                data: updatedData,
                editor: user.id
            };

            // Обновляем в Redux
            dispatch(updateTask(updatedTask));

            // Отправляем на сервер
            if (auth.isAuthenticated && auth.user?.access_token) {
                await $authHost.put(`/apig/tasks/${task.id}`, {
                    data: updatedData,
                    editor: user.id
                });
            }

            setIsEditingResult(false);
        } catch (error) {
            console.error('Ошибка сохранения результата:', error);
        } finally {
            setLoading(false);
        }
    };

    // Вычисление общего статуса задачи на основе статусов всех объектов
    const calculateTaskStatus = (taskData) => {
        if (!taskData?.objects || !taskData?.statuses) {
            return task?.status || 'new';
        }

        // Собираем все статусы объектов
        const objectStatuses = [];
        const objectTypes = ['pillars', 'fiders', 'zvz', 'opn', 'rlnd'];
        
        objectTypes.forEach(type => {
            const objects = taskData.objects[type] || [];
            objects.forEach(objId => {
                const statusKey = type === 'fiders' ? objId : `${type}_${objId}`;
                const status = taskData.statuses[statusKey];
                if (status) {
                    objectStatuses.push(status);
                }
            });
        });

        if (objectStatuses.length === 0) {
            return task?.status || 'new';
        }

        // Проверяем наличие отмененных задач
        const hasCancelled = objectStatuses.some(s => s === 'cancelled');
        
        // Проверяем наличие отложенных задач
        const hasPaused = objectStatuses.some(s => s === 'paused');
        
        // Проверяем все ли завершены или на проверке
        const allDoneOrReview = objectStatuses.every(s => s === 'done' || s === 'review');
        
        // Проверяем есть ли хотя бы одна в работе или завершена
        const hasProgressOrDone = objectStatuses.some(s => s === 'progress' || s === 'done' || s === 'review');
        
        // Если все завершены/на проверке, но есть отмененные - задача может быть завершена
        if (allDoneOrReview && hasCancelled) {
            return 'done';
        }
        
        // Если все завершены/на проверке, но есть отложенные - задача отложена
        if (allDoneOrReview && hasPaused) {
            return 'paused';
        }
        
        // Если все завершены или на проверке - задача завершена
        if (allDoneOrReview) {
            // Если есть хотя бы одна на проверке - задача на проверке
            if (objectStatuses.some(s => s === 'review')) {
                return 'review';
            }
            return 'done';
        }
        
        // Если есть хотя бы одна в работе или завершена - задача в работе
        if (hasProgressOrDone) {
            return 'progress';
        }
        
        // По умолчанию - новая
        return 'new';
    };

    // Обновить статус задачи
    const updateTaskStatus = async (status, reason = '') => {
        if (!task) return;

        setLoading(true);
        try {
            // Помечаем, что статус этого объекта был изменен вручную
            const updatedData = {
                ...task.data,
                statuses: {
                    ...(task.data?.statuses || {}),
                    [`${objectType}_${objectId}`]: status
                },
                // Отслеживаем, какие объекты были изменены вручную
                manuallyChanged: {
                    ...(task.data?.manuallyChanged || {}),
                    [`${objectType}_${objectId}`]: true
                }
            };

            if (reason) {
                updatedData.reasons = {
                    ...(task.data?.reasons || {}),
                    [`${objectType}_${objectId}`]: reason
                };
            }

            // Вычисляем новый общий статус задачи на основе всех статусов объектов
            const newTaskStatus = calculateTaskStatus(updatedData);

            const updatedTask = {
                ...task,
                status: newTaskStatus,
                data: updatedData,
                editor: user.id
            };

            // Обновляем в Redux
            dispatch(updateTask(updatedTask));

            // Отправляем на сервер
            if (auth.isAuthenticated && auth.user?.access_token) {
                await $authHost.put(`/apig/tasks/${task.id}`, {
                    status: newTaskStatus,
                    data: updatedData,
                    editor: user.id
                });
                
                // Обновляем данные задач после успешного изменения статуса
                dispatch(fetchTasks(auth.user?.access_token));
                
                // Если задача типа 'points', обновляем также points tasks через событие карты
                map.fire('tasks:refresh');
                
            }

            // Сбрасываем поля
            setShowRejectField(false);
            setShowPostponeField(false);
            setRejectReason('');
            setPostponeReason('');
        } catch (error) {
            console.error('Ошибка обновления статуса:', error);
        } finally {
            setLoading(false);
        }
    };

    // Получаем доступные переходы статусов
    const getAvailableStatusTransitions = () => {
        const currentStatus = task?.statusCurrent || task?.status;
        if (!currentStatus) return [];
        
        const allowedStatuses = allowedTransitions[currentStatus] || [];
        const creatorId = getCreatorId();
        const responsibleId = getResponsibleId();
        const currentUserId = user?.id;
        const isProjectManager = user?.roles?.includes('project_manager');
        const isCreator = creatorId && Number(creatorId) === Number(currentUserId);
        const isResponsible = responsibleId && Number(responsibleId) === Number(currentUserId);
        
        return statusDefs.filter(status => {
            // Проверяем, что статус разрешен для перехода
            if (!allowedStatuses.includes(status.id) || status.id === currentStatus) {
                return false;
            }
            
            // Для кнопки "Отменено" (cancelled) - видна только постановщику и project_manager
            if (status.id === 'cancelled') {
                // Проверяем, является ли пользователь постановщиком или project_manager
                return isCreator || isProjectManager;
            }
            
            // Для кнопки "Отложено"  - видна только ответственному
            // Проверяем, есть ли статус "Отложено" в названии или id
            if (status.id === 'paused') {
                // Проверяем, является ли пользователь ответственным
                return isResponsible;
            }
            
            // Для остальных статусов - показываем без ограничений
            return true;
        });
    };

    // Обработчик изменения статуса (общий)
    const handleStatusChange = async (newStatus) => {
        if (!task) return;
        
        const currentStatus = task.statusCurrent || task.status;
        
        // Проверяем разрешенные переходы
        if (!allowedTransitions[currentStatus]?.includes(newStatus)) {
            console.warn(`Переход из ${currentStatus} в ${newStatus} не разрешен`);
            return;
        }

        // Для статусов cancelled и paused нужна причина
        if (newStatus === 'cancelled') {
            if (!showRejectField) {
                setShowRejectField(true);
                setShowPostponeField(false);
                return;
            }
            if (!rejectReason.trim()) {
                return;
            }
            await updateTaskStatus(newStatus, rejectReason.trim());
        } else if (newStatus === 'paused') {
            if (!showPostponeField) {
                setShowPostponeField(true);
                setShowRejectField(false);
                return;
            }
            if (!postponeReason.trim()) {
                return;
            }
            await updateTaskStatus(newStatus, postponeReason.trim());
        } else {
            // Для остальных статусов причина не нужна
            await updateTaskStatus(newStatus);
        }
    };

    // Обработчики кнопок (для обратной совместимости)
    const handleComplete = () => {
        handleStatusChange('done');
    };

    const handleReject = () => {
        handleStatusChange('cancelled');
    };

    const handlePostpone = () => {
        handleStatusChange('paused');
    };

    // Проверка, завершена ли задача
    const isTaskCompleted = task?.statusCurrent === 'done' || task?.status === 'done';

    if (!task) {
        return (
            <Box sx={{ textAlign: 'center', padding: '20px', color: 'text.secondary' }}>
                <Typography variant="body2">
                    Задача не найдена для данного объекта
                </Typography>
            </Box>
        );
    }

    const resultHistory = getResultHistory();
    const currentStatus = task.statusCurrent || task.status;
    const taskRejectReason = task.reasonCurrent || task.data?.reasons?.[`${objectType}_${objectId}`] || '';

    return (
        <Box sx={{ maxHeight: '400px', overflowY: 'auto', padding: '4px' }}>
            {/* Блок информации о задаче */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 0.75, 
                    mb: 1, 
                    backgroundColor: darkMode ? '#1e1e1e' : '#f5f5f5',
                    border: `1px solid ${darkMode ? '#424242' : '#e0e0e0'}`
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                        Задача
                    </Typography>
                    {/* Индикатор статуса */}
                    {currentStatus && (
                        <Chip
                            label={
                                currentStatus === 'done' ? 'Завершено' :
                                currentStatus === 'cancelled' ? 'Отменено' :
                                currentStatus === 'paused' ? 'Отложено' :
                                currentStatus === 'progress' ? 'В работе' :
                                currentStatus === 'review' ? 'На проверке' :
                                currentStatus === 'overdue' ? 'Просрочено' :
                                'Новая задача'
                            }
                            size="small"
                            sx={{ 
                                height: '20px', 
                                fontSize: '0.65rem',
                                backgroundColor: 
                                    currentStatus === 'done' ? '#2e7d32' :
                                    currentStatus === 'cancelled' ? '#424242' :
                                    currentStatus === 'paused' ? '#e65100' :
                                    currentStatus === 'progress' ? '#1976d2' :
                                    currentStatus === 'review' ? '#9c27b0' :
                                    currentStatus === 'overdue' ? '#8e2400' :
                                    '#7cb342',
                                color: 'white',
                                fontWeight: 600
                            }}
                        />
                    )}
                </Stack>
                
                {/* Причина отклонения/отложения */}
                {taskRejectReason && (currentStatus === 'cancelled' || currentStatus === 'paused') && (
                    <Box sx={{ 
                        mb: 0.5, 
                        p: 0.5, 
                        backgroundColor: currentStatus === 'cancelled' 
                            ? (darkMode ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)')
                            : (darkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)'),
                        borderRadius: 1,
                        border: `1px solid ${currentStatus === 'cancelled' 
                            ? (darkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)')
                            : (darkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)')}`
                    }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.25 }}>
                            {currentStatus === 'cancelled' ? 'Причина отклонения:' : 'Причина отложения:'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                            {taskRejectReason}
                        </Typography>
                    </Box>
                )}
                
                <Stack spacing={0.5}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Наименование задачи
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {task.title || 'Не указано'}
                        </Typography>
                    </Box>

                    {task.description && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Описание задачи
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {task.description}
                            </Typography>
                        </Box>
                    )}

                    <Stack direction="row" spacing={2} flexWrap="wrap">
                        {task.created_at && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: '0.7rem' }} />
                                    Начало
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                    {formatDate(task.created_at)}
                                </Typography>
                            </Box>
                        )}

                        {task.creator && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon sx={{ fontSize: '0.7rem' }} />
                                    Постановщик
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                    {getCreatorName()}
                                </Typography>
                            </Box>
                        )}

                        {task.deadline && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarIcon sx={{ fontSize: '0.7rem' }} />
                                    Дедлайн
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                    {formatDate(task.deadline)}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            {/* Блок результатов */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 0.75, 
                    mb: 1, 
                    backgroundColor: darkMode ? '#1e1e1e' : '#f5f5f5',
                    border: `1px solid ${darkMode ? '#424242' : '#e0e0e0'}`
                }}
            >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.75, fontSize: '0.85rem' }}>
                    Результат
                </Typography>

                {!isEditingResult && resultHistory.length === 0 && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditingResult(true);
                        }}
                        sx={{ mb: 0.75 }}
                    >
                        Добавить результат
                    </Button>
                )}

                {isEditingResult && (
                    <Box sx={{ mb: 0.75 }}>
                        <TextField
                            multiline
                            rows={3}
                            fullWidth
                            value={resultText}
                            onChange={(e) => setResultText(e.target.value)}
                            placeholder="Введите результат выполнения задачи..."
                            variant="outlined"
                            size="small"
                            sx={{ mb: 0.75 }}
                        />
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    saveResult();
                                }}
                                disabled={!resultText.trim() || loading}
                            >
                                Сохранить
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditingResult(false);
                                    setResultText(resultHistory.length > 0 ? resultHistory[resultHistory.length - 1].text : '');
                                }}
                            >
                                Отмена
                            </Button>
                        </Stack>
                    </Box>
                )}

                {/* История результатов */}
                {resultHistory.length > 0 && (
                    <Box>
                        {resultHistory.map((result, index) => (
                            <Paper
                                key={index}
                                elevation={0}
                                sx={{
                                    p: 0.75,
                                    mb: 0.75,
                                    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
                                    border: `1px solid ${darkMode ? '#424242' : '#e0e0e0'}`,
                                    position: 'relative'
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.25 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem', flex: 1 }}>
                                        {result.text}
                                    </Typography>
                                    {index === resultHistory.length - 1 && !isEditingResult && (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsEditingResult(true);
                                                setResultText(result.text);
                                            }}
                                            sx={{ ml: 0.5, padding: '4px' }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    {formatDateTime(result.date)} - {result.userName || 'Неизвестный пользователь'}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Кнопки действий */}
            {!showRejectField && !showPostponeField && (
                <Box>
                    {/* <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.75, display: 'block' }}>
                        Изменить статус:
                    </Typography> */}
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={"center"}>
                        {getAvailableStatusTransitions().map((status) => (
                            <Button
                                key={status.id}
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(status.id);
                                }}
                                disabled={loading}
                                sx={{
                                    fontSize: '0.7rem',
                                    px: 1,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    backgroundColor: status.color,
                                    color: 'white',
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: status.color,
                                        opacity: 0.85,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 2px 8px ${status.color}40`,
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    },
                                    '&:disabled': {
                                        backgroundColor: darkMode ? '#424242' : '#e0e0e0',
                                        color: darkMode ? '#666' : '#999',
                                        opacity: 0.5,
                                    }
                                }}
                            >
                                {status.title}
                            </Button>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Поле для причины отклонения */}
            {showRejectField && (
                <Box sx={{ mt: 1 }}>
                    <TextField
                        multiline
                        rows={2}
                        fullWidth
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Введите причину отклонения..."
                        variant="outlined"
                        size="small"
                        sx={{ mb: 0.75 }}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleReject();
                            }}
                            disabled={!rejectReason.trim() || loading}
                        >
                            Подтвердить отклонение
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowRejectField(false);
                                setRejectReason('');
                            }}
                        >
                            Отмена
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* Поле для причины отложения */}
            {showPostponeField && (
                <Box sx={{ mt: 1 }}>
                    <TextField
                        multiline
                        rows={2}
                        fullWidth
                        value={postponeReason}
                        onChange={(e) => setPostponeReason(e.target.value)}
                        placeholder="Введите причину отложения..."
                        variant="outlined"
                        size="small"
                        sx={{ mb: 0.75 }}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePostpone();
                            }}
                            disabled={!postponeReason.trim() || loading}
                        >
                            Подтвердить отложение
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowPostponeField(false);
                                setPostponeReason('');
                            }}
                        >
                            Отмена
                        </Button>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default TaskTab;

